import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle, Clock, Users } from "lucide-react";

interface ExecutiveSummaryProps {
  totalTickets: number;
  openTickets: number;
  resolvedTickets: number;
  criticalTickets: number;
  resolutionRate: number;
  avgResolutionHours: number;
  slaPerformance: number;
  backlogChange: number;
}

export const ExecutiveSummary = ({
  totalTickets,
  openTickets,
  resolvedTickets,
  criticalTickets,
  resolutionRate,
  avgResolutionHours,
  slaPerformance,
  backlogChange,
}: ExecutiveSummaryProps) => {
  const getHealthBadge = () => {
    if (slaPerformance >= 90 && resolutionRate >= 80) {
      return <Badge className="bg-green-500/10 text-green-700 dark:text-green-400">Excelente</Badge>;
    }
    if (slaPerformance >= 75 && resolutionRate >= 60) {
      return <Badge className="bg-yellow-500/10 text-yellow-700 dark:text-yellow-400">Atenção</Badge>;
    }
    return <Badge className="bg-red-500/10 text-red-700 dark:text-red-400">Crítico</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Resumo Executivo</h2>
        {getHealthBadge()}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Total Tickets */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Tickets</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalTickets}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {openTickets} em aberto
            </p>
          </CardContent>
        </Card>

        {/* Resolution Rate */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxa de Resolução</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{resolutionRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {resolvedTickets} resolvidos
            </p>
          </CardContent>
        </Card>

        {/* Average Resolution Time */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tempo Médio</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgResolutionHours.toFixed(1)}h</div>
            <p className="text-xs text-muted-foreground mt-1">
              Tempo de resolução
            </p>
          </CardContent>
        </Card>

        {/* SLA Performance */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Performance SLA</CardTitle>
            {slaPerformance >= 90 ? (
              <TrendingUp className="h-4 w-4 text-green-500" />
            ) : (
              <TrendingDown className="h-4 w-4 text-red-500" />
            )}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{slaPerformance.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              Cumprimento de prazos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Critical Alerts */}
      {(criticalTickets > 0 || backlogChange > 10 || slaPerformance < 75) && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Alertas Críticos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {criticalTickets > 0 && (
              <div className="flex items-center justify-between p-2 bg-destructive/10 rounded-md">
                <span className="text-sm">{criticalTickets} tickets críticos pendentes</span>
                <Badge variant="destructive">{criticalTickets}</Badge>
              </div>
            )}
            {backlogChange > 10 && (
              <div className="flex items-center justify-between p-2 bg-yellow-500/10 rounded-md">
                <span className="text-sm">Backlog cresceu {backlogChange} tickets</span>
                <Badge className="bg-yellow-500/20 text-yellow-700 dark:text-yellow-400">
                  +{backlogChange}
                </Badge>
              </div>
            )}
            {slaPerformance < 75 && (
              <div className="flex items-center justify-between p-2 bg-destructive/10 rounded-md">
                <span className="text-sm">Performance SLA abaixo da meta (90%)</span>
                <Badge variant="destructive">{slaPerformance.toFixed(1)}%</Badge>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
